/**
 * WEB-1436 — UTM-метки в долгоживущем хранилище (куки), симметрично рекламным
 * кликам (clickIdCapture.ts). Раньше жили в sessionStorage и умирали с
 * вкладкой: покупка на повторном визите теряла кампанию.
 *
 * Модель — зеркало кликов, без третьей модели:
 * - `qb_utm_lt` — ПОСЛЕДНЕЕ касание: свежий набор utm_* в URL заменяет куку
 *   ЦЕЛИКОМ (частичный мёрж дал бы franken-атрибуцию: source новой кампании +
 *   campaign старой). TTL 90 дней = `_aff_click_id`. Едет в плоских
 *   utm-полях тела события (getStoredUtm), как и раньше.
 * - `qb_utm_ft` — ПЕРВОЕ касание: пишется один раз и никогда не
 *   перезаписывается, TTL 400 дней (потолок Chrome) = `_ft_click_id`. Едет в
 *   теле события внутри clickIds (ftUtmSource…) — тот же приём, что
 *   ftParam/ftValue у кликов: jsonb click_ids, без миграции; денежный путь
 *   поднимает его через CLICK_ID_FIELDS/pickClickIds.
 *
 * Обратная совместимость: legacy-ключ sessionStorage читается, когда куки ещё
 * нет (визит, начавшийся до релиза), и мигрирует в last-touch куку. Больше в
 * sessionStorage НЕ пишем. First-touch из legacy НЕ засеивается — первое
 * касание задним числом не восстановить (комментарий _ft_click_id).
 *
 * Согласие: как у кликов — first-party атрибуционные куки пишутся без
 * ожидания баннера (captureClickIds точно так же). Гейт согласия стоит там,
 * где клики его ждут: загрузка пикселей (pixelLoader, строго granted) и
 * серверные конверсии (бэкенд — арбитр по полю consent события).
 */

/** Legacy sessionStorage-ключ (до WEB-1436). Только чтение + миграция. */
export const UTM_STORAGE_KEY = 'qb_utm_params';

/** Last-touch UTM-набор (JSON UtmParams). TTL зеркалит `_aff_click_id`. */
export const UTM_LT_COOKIE = 'qb_utm_lt';
const UTM_LT_TTL_DAYS = 90;

/** First-touch UTM-набор (JSON UtmParams), write-once. TTL = `_ft_click_id`. */
export const UTM_FT_COOKIE = 'qb_utm_ft';
const UTM_FT_TTL_DAYS = 400;

/**
 * Предохранитель длины значения: ftUtm* едут внутрь clickIds, где бэкенд
 * валидирует MaxLength(255) при forbidNonWhitelisted — слишком длинное
 * значение отбило бы ВСЁ событие 400-й (шрам WEB-933 п.3).
 */
const MAX_VALUE_LENGTH = 255;

export interface UtmParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

const EMPTY: UtmParams = {
  utmSource: undefined,
  utmMedium: undefined,
  utmCampaign: undefined,
  utmTerm: undefined,
  utmContent: undefined,
};

let cached: UtmParams | null = null;

function readCookie(name: string): string | undefined {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
  } catch {
    /* ignore — cookie write is best-effort, in-memory cache still wins */
  }
}

function sanitize(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return value.slice(0, MAX_VALUE_LENGTH);
}

function parseUtmJson(raw: string | undefined | null): UtmParams | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const pick = (key: keyof UtmParams): string | undefined => {
      const v = parsed[key];
      return typeof v === 'string' && v.length > 0 ? sanitize(v) : undefined;
    };
    return {
      utmSource: pick('utmSource'),
      utmMedium: pick('utmMedium'),
      utmCampaign: pick('utmCampaign'),
      utmTerm: pick('utmTerm'),
      utmContent: pick('utmContent'),
    };
  } catch {
    return null;
  }
}

function readFromCookie(name: string): UtmParams | null {
  return parseUtmJson(readCookie(name));
}

/** Legacy sessionStorage (визиты, начавшиеся до релиза WEB-1436). */
function readFromLegacyStorage(): UtmParams | null {
  try {
    return parseUtmJson(sessionStorage.getItem(UTM_STORAGE_KEY));
  } catch {
    return null;
  }
}

function readFromUrl(): UtmParams {
  try {
    const params = new URLSearchParams(window.location.search);
    const pick = (key: string): string | undefined =>
      sanitize(params.get(key) ?? undefined);
    return {
      utmSource: pick('utm_source'),
      utmMedium: pick('utm_medium'),
      utmCampaign: pick('utm_campaign'),
      utmTerm: pick('utm_term'),
      utmContent: pick('utm_content'),
    };
  } catch {
    return { ...EMPTY };
  }
}

function hasAnyUtm(p: UtmParams): boolean {
  return Boolean(
    p.utmSource || p.utmMedium || p.utmCampaign || p.utmTerm || p.utmContent,
  );
}

/** Только присутствующие ключи — для компактной куки. */
function compact(p: UtmParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of [
    'utmSource',
    'utmMedium',
    'utmCampaign',
    'utmTerm',
    'utmContent',
  ] as const) {
    const v = p[key];
    if (v) out[key] = v;
  }
  return out;
}

function writeLastTouch(p: UtmParams): void {
  writeCookie(UTM_LT_COOKIE, JSON.stringify(compact(p)), UTM_LT_TTL_DAYS);
}

export function captureUtmFromUrl(): void {
  const fromUrl = readFromUrl();
  if (hasAnyUtm(fromUrl)) {
    // Last-touch: свежий набор заменяет куку целиком (зеркало _aff_click_id).
    cached = fromUrl;
    writeLastTouch(fromUrl);
    // First-touch: только если ещё не было (зеркало _ft_click_id, write-once).
    if (!readCookie(UTM_FT_COOKIE)) {
      writeCookie(
        UTM_FT_COOKIE,
        JSON.stringify(compact(fromUrl)),
        UTM_FT_TTL_DAYS,
      );
    }
    return;
  }
  // Меток в URL нет: кука (если есть) остаётся как была; legacy sessionStorage
  // мигрирует в куку, чтобы визит, начавшийся до релиза, пережил вкладку.
  if (cached && hasAnyUtm(cached)) return;
  const fromCookie = readFromCookie(UTM_LT_COOKIE);
  if (fromCookie && hasAnyUtm(fromCookie)) {
    cached = fromCookie;
    return;
  }
  const legacy = readFromLegacyStorage();
  if (legacy && hasAnyUtm(legacy)) {
    cached = legacy;
    writeLastTouch(legacy);
  }
}

export function getStoredUtm(): UtmParams {
  if (cached && hasAnyUtm(cached)) return cached;
  const fromCookie = readFromCookie(UTM_LT_COOKIE);
  if (fromCookie && hasAnyUtm(fromCookie)) {
    cached = fromCookie;
    return cached;
  }
  const legacy = readFromLegacyStorage();
  if (legacy && hasAnyUtm(legacy)) {
    cached = legacy;
    // Миграция: значение переезжает в куку и переживает закрытие вкладки.
    writeLastTouch(legacy);
    return cached;
  }
  return { ...EMPTY };
}

/** Первое касание UTM ({...} с известными полями) или null (меток не было). */
export function getFirstTouchUtm(): UtmParams | null {
  const parsed = readFromCookie(UTM_FT_COOKIE);
  return parsed && hasAnyUtm(parsed) ? parsed : null;
}

/**
 * Первое касание UTM в форме полей clickIds тела события (ftUtmSource…) —
 * прицепом к getStoredClickIds() в trackEvent.ts. Пустой объект, когда
 * первого касания нет: отсутствующее поле лучше пустого (тот же принцип,
 * что pickClickIds на бэке).
 */
export function getFirstTouchUtmClickIdFields(): Record<string, string> {
  const ft = getFirstTouchUtm();
  if (!ft) return {};
  const out: Record<string, string> = {};
  if (ft.utmSource) out.ftUtmSource = ft.utmSource;
  if (ft.utmMedium) out.ftUtmMedium = ft.utmMedium;
  if (ft.utmCampaign) out.ftUtmCampaign = ft.utmCampaign;
  if (ft.utmTerm) out.ftUtmTerm = ft.utmTerm;
  if (ft.utmContent) out.ftUtmContent = ft.utmContent;
  return out;
}

export function resetUtmCache(): void {
  cached = null;
}
